import { useCallback } from 'react'

import { useQuery } from '@redwoodjs/web'

import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_EMAIL_ANALYTICS_HERO_STATS } from 'src/graphql/emailAnalytics.graphql'
import {
  HeroStatCard,
  type HeroStatDelta,
} from 'src/pages/AnalyticsPage/components/HeroStatCard'
import {
  buildDelta,
  formatCount,
  percentOf,
  toDateRangeBounds,
} from 'src/pages/AnalyticsPage/components/heroStatHelpers'
import { useRefetchOnKeyChange } from 'src/pages/AnalyticsPage/components/trendChartHelpers'

import type { DateRangeFilter, EmailLogFiltersInput } from 'types/graphql'

/** Formats seconds as hours and minutes only (no seconds). */
const formatTimeSaved = (seconds: number | null | undefined): string => {
  if (seconds == null) return '—'
  const s = Math.max(0, Math.round(seconds))
  if (s === 0) return '0m'
  const hours = Math.floor(s / 3600)
  const minutes = Math.floor((s % 3600) / 60)
  if (hours > 0) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`
  return `${minutes}m`
}

const buildTimeSavedDelta = (
  current: number | null | undefined,
  previous: number | null | undefined,
  currentDateRange?: DateRangeFilter | null
): HeroStatDelta | undefined => {
  if (current == null || previous == null) return undefined

  const rawChange = current - previous
  const isFlat = Math.abs(rawChange) < 0.05
  const valueWentUp = rawChange > 0
  const direction: HeroStatDelta['direction'] = isFlat
    ? 'neutral'
    : valueWentUp
    ? 'up'
    : 'down'

  const label = formatTimeSaved(Math.abs(rawChange))
  const tooltipDelta = `${rawChange >= 0 ? '+' : '−'}${formatTimeSaved(
    Math.abs(rawChange)
  )}`

  let tooltip: string
  const dateRangeBounds = toDateRangeBounds(currentDateRange)
  if (dateRangeBounds) {
    const dayMs = 24 * 60 * 60 * 1000
    const numDays = Math.round(
      (dateRangeBounds.lte.getTime() - dateRangeBounds.gte.getTime()) / dayMs
    )
    tooltip =
      `This period (${numDays} days): ${formatTimeSaved(current)}\n` +
      `Prior period (${numDays} days): ${formatTimeSaved(previous)}\n` +
      `Δ ${tooltipDelta}`
  } else {
    tooltip =
      `This period: ${formatTimeSaved(current)}\n` +
      `Prior period: ${formatTimeSaved(previous)}\n` +
      `Δ ${tooltipDelta}`
  }

  return {
    direction,
    label,
    tooltip,
  }
}

const EmailAnalyticsHeroStrip = ({
  filters,
  refreshKey,
}: {
  filters: EmailLogFiltersInput
  refreshKey: number
}) => {
  const onError = useCallback(() => {
    toast('Failed to load email analytics summary', 'error')
  }, [])

  const { data, loading, refetch } = useQuery(GET_EMAIL_ANALYTICS_HERO_STATS, {
    variables: { filters },
    onError,
    notifyOnNetworkStatusChange: true,
  })

  useRefetchOnKeyChange(refetch, refreshKey)

  const stats = data?.emailAnalyticsHeroStats
  const current = stats?.current
  const previous = stats?.previous

  const totalDelta = buildDelta({
    current: current?.totalEmail,
    previous: previous?.totalEmail,
    format: 'percent-change',
    unit: 'count',
    currentDateRange: filters.dateRange,
  })
  const outboundDelta = buildDelta({
    current: current?.outboundEmail,
    previous: previous?.outboundEmail,
    format: 'percent-change',
    unit: 'count',
    currentDateRange: filters.dateRange,
  })
  const inboundDelta = buildDelta({
    current: current?.inboundEmail,
    previous: previous?.inboundEmail,
    format: 'percent-change',
    unit: 'count',
    currentDateRange: filters.dateRange,
  })
  const automatedDelta = buildDelta({
    current: current?.automatedEmail,
    previous: previous?.automatedEmail,
    format: 'percent-change',
    unit: 'count',
    currentDateRange: filters.dateRange,
  })
  const timeSavedDelta = buildTimeSavedDelta(
    current?.timeSavedSeconds,
    previous?.timeSavedSeconds,
    filters.dateRange
  )

  const outboundShare = percentOf(current?.outboundEmail, current?.totalEmail)
  const inboundShare = percentOf(current?.inboundEmail, current?.totalEmail)
  const automatedShare = percentOf(current?.automatedEmail, current?.outboundEmail)

  return (
    <div
      className="mb-4 grid grid-cols-5 gap-3"
      data-testid="email-analytics-hero-strip"
    >
      <HeroStatCard
        label="Total Email"
        value={formatCount(current?.totalEmail)}
        loading={loading}
        loadingShowDelta
        delta={totalDelta}
        info={
          'All email in the period (inbound + outbound, automated + manual).' +
          (current
            ? `\n\n${formatCount(current.inboundEmail)} inbound · ${formatCount(
                current.outboundEmail
              )} outbound`
            : '')
        }
      />
      <HeroStatCard
        label="Outbound Email"
        value={formatCount(current?.outboundEmail)}
        loading={loading}
        loadingShowDelta
        delta={outboundDelta}
        info={
          'Email we sent to candidates.' +
          (outboundShare ? `\n\n${outboundShare} of total email` : '')
        }
      />
      <HeroStatCard
        label="Inbound Email"
        value={formatCount(current?.inboundEmail)}
        loading={loading}
        loadingShowDelta
        delta={inboundDelta}
        info={
          'Email candidates sent to us.' +
          (inboundShare ? `\n\n${inboundShare} of total email` : '')
        }
      />
      <HeroStatCard
        label="Automated Email"
        value={formatCount(current?.automatedEmail)}
        loading={loading}
        loadingShowDelta
        delta={automatedDelta}
        info={
          'Email sent via automated rules, bulk actions, or other system actions.' +
          (automatedShare ? `\n\n${automatedShare} of outbound email` : '')
        }
      />
      <HeroStatCard
        label="Time Saved"
        value={formatTimeSaved(current?.timeSavedSeconds)}
        loading={loading}
        loadingShowDelta
        delta={timeSavedDelta}
        featured
        info="Total time saved from email — automated rules, bulk actions, and other system actions (1 minute per email)."
      />
    </div>
  )
}

export default EmailAnalyticsHeroStrip
